import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Button, Autocomplete, DynamicField } from '@totalsoft/rocket-ui';
import { GridActionsWrapper } from './ScheduleTypeCellParamTemplateDetailsStyles';
import ScheduleTypeCellParamTemplateDetailsCalendarView from './ScheduleTypeCellParamTemplateDetailsCalendarView';
import ScheduleTypeCellParamTemplateDetailsListView from './ScheduleTypeCellParamTemplateDetailsListView';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_SCHEDULES_WRITE } from 'constants/rightsCodes';
import { drop } from 'ramda';
import { get, over } from '@totalsoft/change-tracking-react';
import { seasonalityTypeIds } from '../../constants';

export const ScheduleTypeCellParamTemplateDetailsComponent = ({ lens, columns, saving, onResetDetailValues, readOnly }) => {
  const [t] = useTranslation();
  const canWrite = usePermission(ADMIN_SCHEDULES_WRITE);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const tableColumns = useMemo(() => {
    let tableLocal = [
      {
        cellParamName: 'selected',
        cellParamDisplayName: 'ScheduleTypeCellParamTemplate.Details.Selected',
        controlType: 'Checkbox',
        readOnly: false
      },
      {
        cellParamName: 'MonthNo',
        cellParamDisplayName: 'ScheduleTypeCellParamTemplate.Details.MonthNo',
        controlType: 'Numeric',
        inputProps: { decimalScale: 0 },
        readOnly: true
      }
    ];
    
    columns?.scheduleTypeCellParamTemplateColumns?.forEach(item => {
      let itemToAdd = {
        cellParamName: item.cellParamName,
        cellParamDisplayName: item.cellParamName,
        readOnly: true
      };

      if (item.cellParamTypeName === 'Procent6') {
        itemToAdd.controlType = 'Numeric'
        itemToAdd.inputProps = { decimalScale: 6 };
      }
      if (item.cellParamTypeName === 'String') {
        itemToAdd.controlType = 'Text'
      }
      if (item.cellParamTypeName === 'Numeric') {
        itemToAdd.controlType = 'Numeric'
        itemToAdd.inputProps = { decimalScale: 0 };
      }
      if (item.cellParamTypeName === 'Data') {
        itemToAdd.controlType = 'Date'
      }
      if (item.cellParamTypeName === 'Procent') {
        itemToAdd.controlType = 'Numeric'
        itemToAdd.inputProps = { decimalScale: 4 };
      }
      if (item.cellParamTypeName === 'Amount') {
        itemToAdd.controlType = 'Numeric'
        itemToAdd.inputProps = { decimalScale: 2 };
      }
      if (item.cellParamTypeName === 'Intreg') {
        itemToAdd.controlType = 'Integer'
      }
      if (item.cellParamTypeName === 'Boolean') {
        itemToAdd.controlType = 'Checkbox'
      }
      tableLocal.push(itemToAdd);
    });

    return tableLocal;
  }, [columns]);

  const { scheduleCellParamColumns, columnSelectionOptions } = useMemo(() => {
    const scheduleCellParamColumns = tableColumns.map(x => ({ ...x, cellParamDisplayName: t(x.cellParamDisplayName) }));
    const columnSelectionOptions = drop(2, scheduleCellParamColumns);
    return { scheduleCellParamColumns, columnSelectionOptions };
  }, [t, tableColumns]);
  const isCalendarView = (lens.seasonalityTypeId |> get) === seasonalityTypeIds.Seasonal;
  return (
    <>
      <Grid container spacing={3} alignItems={'flex-end'}>
        <Grid item xs={12} sm={4}>
          {!isCalendarView && (
            <Autocomplete
              id={'scheduleTypeCellParamTemplateSeasonalSettingsCard_scheduleTypeCellParam'}
              fullWidth
              value={selectedColumn}
              valueKey='cellParamName'
              labelKey='cellParamDisplayName'
              label={t('ScheduleTypeCellParamTemplate.Details.ScheduleTypeCellParam')}
              options={columnSelectionOptions}
              onChange={setSelectedColumn}
              isClearable={false}
              disabled={!(lens.isEditable |> get) || !canWrite || readOnly}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={4}>
          {!isCalendarView && selectedColumn ? (
            <DynamicField
              id='cellParamIdInput'
              controlType={selectedColumn.controlType}
              inputProps={selectedColumn.inputProps}
              label={selectedColumn.controlType === 'Checkbox' ? '' : t('ScheduleTypeCellParamTemplate.Details.ScheduleTypeCellParamValue')}
              value={selectedColumn.paramValue}
              onChange={event => setSelectedColumn(prev => ({ ...prev, paramValue: event }))}
              disabled={!canWrite || readOnly}
            />
          ) : null}
        </Grid>

        <GridActionsWrapper item xs={12} sm={4} alignItems='center' justifyContent='right'>
          {!isCalendarView && (
            <>
              <Button
                id={'scheduleTypeCellParamTemplateSeasonalSettingsCard_applyBtn'}
                color='primary'
                variant='contained'
                disabled={!(lens.isEditable |> get) || !canWrite || !selectedColumn || readOnly}
                onClick={() =>
                  over(lens.scheduleTypeCellParamTemplateDetails, prev =>
                    prev.map(x => ({
                      ...x,
                      [selectedColumn.cellParamName]: x.selected ? selectedColumn.paramValue : x[selectedColumn.cellParamName]
                    }))
                  )
                }
              >
                {t('Apply')}
              </Button>
              <Button
                id={'scheduleTypeCellParamTemplateSeasonalSettingsCard_resetBtn'}
                color='primary'
                variant='outlined'
                onClick={onResetDetailValues}
                disabled={!(lens.isEditable |> get) || !canWrite || readOnly}
              >
                {t('Cancel')}
              </Button>
            </>
          )}
        </GridActionsWrapper>
      </Grid>
      <Grid container mt={3}>
        <Grid item xs={12}>
          {isCalendarView ? (
            <ScheduleTypeCellParamTemplateDetailsCalendarView
              scheduleCellParamColumns={scheduleCellParamColumns}
              lens={lens}
              isEditable={(lens.isEditable |> get) && canWrite && !saving && !readOnly}
              onResetDetailValues={onResetDetailValues}
            />
          ) : (
            <ScheduleTypeCellParamTemplateDetailsListView
              scheduleCellParamColumns={scheduleCellParamColumns}
              lens={lens}
              isEditable={(lens.isEditable |> get) && canWrite && !saving && !readOnly}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

ScheduleTypeCellParamTemplateDetailsComponent.propTypes = {
  lens: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  saving: PropTypes.bool.isRequired,
  onResetDetailValues: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired
};

export default ScheduleTypeCellParamTemplateDetailsComponent;
